import "@loot-os/react/styles.css";
import "./appearanceLayout.css";

import {
  compactOpenerThemeForKind,
  DEFAULT_OPENER_KIND,
  kindTokenFallback,
  OPENER_COLOR_LABELS,
  OPENER_COLOR_ROLES,
  OPENER_KIND_INFO,
  OPENER_KIND_TOKENS,
  OPENER_STYLE_INHERIT,
  type OpenerColorRole,
  resolveShopifyOpenerTheme,
  SAMPLE_OPENER_BOX,
  type ShopifyOpenerThemeDraft,
  STOREFRONT_RARITY_TIERS,
  shopifyOpenerThemeDraftOf,
  themeForKind,
} from "@loot-os/api-contract";
import { type BoxOpener, LootBoxOpener, resolveTheme } from "@loot-os/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { fieldValue } from "./fieldValue";
import { previewClient } from "./previewClient";
import { useTRPC } from "./trpc";

/** The opener's stock palette, which an unset color keeps. */
const STOCK_COLORS = resolveTheme().colors;

/**
 * How the opener looks when a customer reveals a box in the store: the colors of the animation
 * the store's style opens with, then its colors, rarity colors, corner radius and font, on the
 * left, with the style, the save buttons and the opener itself spinning one of the store's boxes
 * (a sample box while it sells none) pinned on the right while they scroll, in the store's opener
 * style (nothing is opened or charged). Blank fields keep the opener's own palette, and a blank button color follows the
 * store's brand color. Nothing is written until Save, which saves the style and the theme
 * together; the preview follows the unsaved form. The tenant edits the same theme on the Loot OS
 * dashboard's Shopify page.
 */
export function AppearanceScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const appearance = useQuery(trpc.appearance.get.queryOptions());
  const [draft, setDraft] = useState<ShopifyOpenerThemeDraft | null>(null);
  // The style select's value while it differs from the saved one; null = the saved one.
  const [stylePick, setStylePick] = useState<string | null>(null);
  const saveTheme = useMutation(trpc.appearance.set.mutationOptions());
  const saveStyle = useMutation(trpc.appearance.setStyle.mutationOptions());
  const saving = saveTheme.isPending || saveStyle.isPending;

  const data = appearance.data;
  const savedStyleValue = data?.openerStyleId ?? OPENER_STYLE_INHERIT;
  const styleValue = stylePick ?? savedStyleValue;
  const styleId = styleValue === OPENER_STYLE_INHERIT ? null : styleValue;
  const styleChanged = styleValue !== savedStyleValue;
  const dirty = draft !== null || styleChanged;
  // The style boxes that pick none open with, an unsaved pick included: the store's own, else the
  // one it inherits.
  const pickedStyle = data?.openerStyles.find(
    (option) => option.id === (styleId ?? data.inheritedOpenerStyleId),
  );
  const kind = pickedStyle?.kind ?? DEFAULT_OPENER_KIND;
  // The saved style arrives resolved; an unsaved pick is built from the style list the way the
  // server resolves one, wearing the environment's logo and brand name as the saved one does.
  const previewOpener = useMemo<BoxOpener | undefined>(() => {
    if (!data || !styleChanged || !pickedStyle) return data?.opener;
    const overrides = themeForKind(pickedStyle.kind, pickedStyle.theme);
    return {
      styleId: pickedStyle.id,
      kind: pickedStyle.kind,
      ...(Object.keys(overrides).length ? { theme: overrides } : {}),
      ...(data.opener.logoSvg ? { logoSvg: data.opener.logoSvg } : {}),
      ...(data.opener.brandName ? { brandName: data.opener.brandName } : {}),
    };
  }, [data, styleChanged, pickedStyle]);

  const stored = data?.theme ?? null;
  const brandColor = appearance.data?.brandColor ?? null;
  const form = draft ?? shopifyOpenerThemeDraftOf(stored);
  const theme = compactOpenerThemeForKind(form, kind);
  const themeKey = JSON.stringify(theme);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the serialized theme
  const openerTheme = useMemo(
    () => resolveShopifyOpenerTheme(theme, brandColor),
    [themeKey, brandColor],
  );
  const shopBox = appearance.data?.previewBox ?? null;
  const previewBox = shopBox ?? SAMPLE_OPENER_BOX;
  const createClient = useMemo(() => previewClient(previewBox), [previewBox]);

  if (appearance.isLoading) return <s-paragraph>Loading…</s-paragraph>;
  if (appearance.error || !appearance.data) {
    return (
      <s-banner tone="critical">
        {appearance.error?.message ?? "Could not load the appearance."}
      </s-banner>
    );
  }

  const accentFallback = resolveShopifyOpenerTheme(null, brandColor)?.colors;
  const fallbackFor = (role: OpenerColorRole) =>
    (role === "accent" || role === "accentForeground" ? accentFallback?.[role] : undefined) ??
    STOCK_COLORS[role];
  const edit = (next: Partial<ShopifyOpenerThemeDraft>) => setDraft({ ...form, ...next });
  const discard = () => {
    setDraft(null);
    setStylePick(null);
  };
  /** Writes only what changed: the theme, the style, or both. */
  const saveChanges = async () => {
    const [themeWrite, styleWrite] = await Promise.allSettled([
      draft === null ? null : saveTheme.mutateAsync({ theme }),
      styleChanged ? saveStyle.mutateAsync({ styleId }) : null,
    ]);
    // Refetch before dropping the unsaved form, so the form and the preview never flash back to
    // the old values (a different kind would remount the opener twice).
    await queryClient.invalidateQueries({ queryKey: trpc.appearance.get.queryKey() });
    // A refusal is already a toast (queryClient's MutationCache), and the form keeps only what
    // it refused.
    if (themeWrite.status === "fulfilled") setDraft(null);
    if (styleWrite.status === "fulfilled") setStylePick(null);
    if (themeWrite.status === "rejected" || styleWrite.status === "rejected") return;
    shopify.toast.show(
      themeWrite.value?.theme === null && !styleChanged
        ? "Back to your brand color."
        : "Appearance saved.",
    );
  };
  const inheritedName = appearance.data.openerStyles.find(
    (style) => style.id === appearance.data.inheritedOpenerStyleId,
  )?.name;

  const kindTokens = OPENER_KIND_TOKENS[kind];

  return (
    <div className="appearance-layout">
      <s-stack gap="large">
        <s-paragraph>
          How the box opener looks when a customer reveals a box in your store. Leave a field blank
          to keep the default; a blank button color uses your brand color.
        </s-paragraph>

        {kindTokens.length > 0 && (
          <s-stack gap="base">
            <s-heading>{`${OPENER_KIND_INFO[kind].label} colors`}</s-heading>
            {kindTokens.map((token) => (
              <s-color-field
                key={token.key}
                label={token.label}
                placeholder={kindTokenFallback(token, fallbackFor)}
                value={form.kind[token.key] ?? ""}
                onInput={(e) => edit({ kind: { ...form.kind, [token.key]: fieldValue(e) } })}
                onChange={(e) => edit({ kind: { ...form.kind, [token.key]: fieldValue(e) } })}
              />
            ))}
          </s-stack>
        )}

        <s-stack gap="base">
          <s-heading>Colors</s-heading>
          {OPENER_COLOR_ROLES.map((role) => (
            <s-color-field
              key={role}
              label={OPENER_COLOR_LABELS[role]}
              placeholder={fallbackFor(role)}
              value={form.colors[role] ?? ""}
              onInput={(e) => edit({ colors: { ...form.colors, [role]: fieldValue(e) } })}
              onChange={(e) => edit({ colors: { ...form.colors, [role]: fieldValue(e) } })}
            />
          ))}
        </s-stack>

        <s-stack gap="base">
          <s-heading>Shape and type</s-heading>
          <s-text-field
            label="Corner radius"
            placeholder="16px"
            details="A length like 12px. 0 gives square corners."
            value={form.radius}
            onInput={(e) => edit({ radius: fieldValue(e) })}
          />
          <s-text-field
            label="Font"
            placeholder="Your theme's font"
            details="A font your theme already loads, like Inter, sans-serif."
            value={form.fontFamily}
            onInput={(e) => edit({ fontFamily: fieldValue(e) })}
          />
        </s-stack>

        <s-stack gap="base">
          <s-heading>Rarity colors</s-heading>
          {STOREFRONT_RARITY_TIERS.map((tier) => (
            <s-color-field
              key={tier.key}
              label={tier.label}
              placeholder={tier.default}
              value={form.rarity[tier.key] ?? ""}
              onInput={(e) => edit({ rarity: { ...form.rarity, [tier.key]: fieldValue(e) } })}
              onChange={(e) => edit({ rarity: { ...form.rarity, [tier.key]: fieldValue(e) } })}
            />
          ))}
        </s-stack>
      </s-stack>

      <div className="appearance-aside">
        <s-stack gap="large">
          <s-stack gap="base">
            <s-heading>Opener style</s-heading>
            <s-select
              label="Style"
              details="The animation, and any colors it sets over yours, for boxes that pick no style of their own."
              value={styleValue}
              disabled={saving || undefined}
              onChange={(e) => {
                const value = fieldValue(e);
                setStylePick(value === savedStyleValue ? null : value);
              }}
            >
              <s-option value={OPENER_STYLE_INHERIT}>
                {`Your Loot OS default${inheritedName ? ` (${inheritedName})` : ""}`}
              </s-option>
              {appearance.data.openerStyles.map((style) => (
                <s-option key={style.id} value={style.id}>
                  {style.name}
                </s-option>
              ))}
            </s-select>
          </s-stack>

          <s-stack direction="inline" gap="base">
            <s-button
              variant="primary"
              disabled={!dirty || undefined}
              loading={saving || undefined}
              onClick={() => void saveChanges()}
            >
              Save
            </s-button>
            <s-button disabled={!dirty || saving || undefined} onClick={discard}>
              Discard
            </s-button>
            <s-button
              variant="tertiary"
              disabled={theme === null || saving || undefined}
              // Clears the form to the brand color; Save confirms it.
              onClick={() => setDraft(stored === null ? null : shopifyOpenerThemeDraftOf(null))}
            >
              Reset to brand color
            </s-button>
          </s-stack>
        </s-stack>

        <div className="appearance-preview">
          <s-stack gap="base">
            <s-heading>Preview</s-heading>
            <LootBoxOpener
              key={previewBox.id}
              boxId={previewBox.id}
              createClient={createClient}
              // Non-empty so the SDK calls the factory; the factory answers locally.
              publishableKey="preview"
              sessionToken={null}
              theme={openerTheme}
              opener={previewOpener}
              maxQuantity={1}
              defaultQuantity={1}
              currency={{
                name: "dollar",
                namePlural: "dollars",
                symbol: "$",
                multiplier: 100,
                iconSvg: "",
              }}
              sounds={false}
              showFairness={false}
            />
            {shopBox === null && (
              <s-paragraph color="subdued">
                A sample box. Once a box is on sale in your store, the preview spins it.
              </s-paragraph>
            )}
          </s-stack>
        </div>
      </div>
    </div>
  );
}
